import { useNavigate } from 'react-router-dom';
import type { IconName } from '@capsico/ui';
import type { NotifyCategory } from '@capsico/contracts';
import { useI18n } from '../i18n';
import { useInbox } from '../hooks/useInbox';
import { useSession } from '../hooks/useSession';
import { orderTime } from '../utils/format';
import { VirtualList } from '../components/VirtualList';
import { INBOX_ROW_HEIGHT, InboxRow } from '../components/Inbox';
import {
  Button,
  Card,
  EmptyState,
  IconButton,
  ListSkeleton,
  Notice,
  PageHeader,
} from '../components/ui';

const CATEGORY_ICON: Record<NotifyCategory, IconName> = {
  orders: 'bag',
  support: 'info',
  account: 'wallet',
  offers: 'tag',
  reminders: 'clock',
};

/**
 * Notifications inbox (W18): every message Capsico sent, newest first, unread ones tinted with a
 * yellow dot. Tapping marks it read and opens its screen (order, offer, help chat…). "Mark all
 * read" in the header. Long inboxes are virtualised; older pages load on demand.
 */
export function InboxPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const session = useSession();
  const inbox = useInbox(session.signedIn, { failed: t('inbox.failed') });

  return (
    <div className="min-h-dvh bg-page pb-10">
      <PageHeader
        title={t('inbox.title')}
        onBack={() => navigate(-1)}
        backLabel={t('common.back')}
        action={
          <IconButton
            icon="settings"
            label={t('inbox.settings')}
            onClick={() => navigate('/account/notifications')}
          />
        }
      />
      <div className="flex flex-col gap-4 px-4 pt-4">
        {inbox.unread > 0 && (
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-ink">
              {t('inbox.unreadCount', { count: inbox.unread })}
            </p>
            <Button size="sm" tone="ghost" busy={inbox.markingAll} onClick={inbox.markAllRead}>
              {t('inbox.readAll')}
            </Button>
          </div>
        )}

        {inbox.failed ? (
          <Notice
            tone="negative"
            supportRef
            title={t('common.wrong')}
            action={
              <Button size="sm" tone="outline" onClick={inbox.retry}>
                {t('common.retry')}
              </Button>
            }
          >
            {inbox.error}
          </Notice>
        ) : inbox.loading ? (
          <Card>
            <ListSkeleton rows={5} />
          </Card>
        ) : inbox.rows.length === 0 ? (
          <Card>
            <EmptyState icon="bell" title={t('inbox.empty')}>
              {t('inbox.emptyBody')}
            </EmptyState>
          </Card>
        ) : (
          <>
            <Card flush>
              <VirtualList
                items={inbox.rows}
                rowHeight={INBOX_ROW_HEIGHT}
                getKey={(row) => row.id}
                renderRow={(row) => (
                  <InboxRow
                    icon={CATEGORY_ICON[row.category]}
                    category={t(`notify.cat.${row.category}`)}
                    title={row.title}
                    body={row.body}
                    when={orderTime(row.createdAt)}
                    unread={row.readAt === null}
                    unreadLabel={t('inbox.new')}
                    onOpen={() => {
                      const path = inbox.open(row);
                      if (path) navigate(path);
                    }}
                  />
                )}
              />
            </Card>
            {inbox.hasMore ? (
              <Button tone="outline" full busy={inbox.loadingMore} onClick={inbox.loadMore}>
                {t('inbox.loadMore')}
              </Button>
            ) : (
              <p className="text-center text-caption text-ink-3">{t('inbox.end')}</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
